/** TĂIETURA PE ETAJ a machetei de situație (Cătălin, 13.10.2026): alegi un
 *  etaj, tot ce e deasupra lui dispare, iar etajul se vede pe dinăuntru — cu
 *  pereții, pardoselile pe tipul apartamentului, mobilierul și numele
 *  camerelor (v. `interior3d.ts`).
 *
 *  CUM, FĂRĂ SĂ REFACĂ MACHETA. Fațada e lipită pe material peste toate
 *  etajele (o plasă pe culoare), deci un etaj nu se poate ascunde ca obiect.
 *  Se ascunde cu un PLAN DE TĂIERE orizontal, pus o dată pe materialele
 *  clădirii: deasupra lui nu se desenează nimic (nici umbra). Corpul plin e
 *  câte o plasă pe etaj, deci al etajului ales și cele de deasupra se ascund;
 *  fața de sus a corpului de dedesubt e pardoseala. Încăperea întunecată din
 *  spatele geamurilor (`interior`) se taie la pardoseala etajului: altfel ar
 *  sta, neagră, în camere.
 *
 *  PLANUL STĂ PE MATERIALE DE LA ÎNCEPUT, departe în sus când nu se taie
 *  nimic: schimbarea etajului mută doar o cifră, nu recompilează shaderele —
 *  trecerea de la un etaj la altul e pe loc.
 *
 *  INTERIOARELE se fac la cerere și se țin minte (câteva etaje), iar etajele
 *  vecine se pregătesc în timpul liber, ca urcatul și coborâtul să fie pe loc. */
import * as THREE from "three";
import type { StoreyPlan } from "../canonical";
import type { Fatada3 } from "./sit3d";
import { construiesteInteriorul, type InteriorEtaj, type OptiuniInterior } from "./interior3d";
import { cotaEtajului, inPoligon, type ApartamentEtaj } from "./interiorEtaj";

/** Cota planului când nu se taie nimic: deasupra oricărei clădiri. */
const FARA_TAIERE = 1e5;
/** Câte etaje își țin interiorul făcut. */
const ETAJE_TINUTE = 6;

export class TaieturaEtaj {
  /** Păstrează ce e SUB tăietură (z ≤ sus). */
  readonly sus = new THREE.Plane(new THREE.Vector3(0, 0, -1), FARA_TAIERE);
  /** Pentru încăperea din spatele geamurilor: z ≤ pardoseala etajului ales. */
  readonly jos = new THREE.Plane(new THREE.Vector3(0, 0, -1), FARA_TAIERE);
  /** Cotele etajului tăiat; `null` = toată clădirea. */
  cote: { nivel: number; jos: number; sus: number } | null = null;
  /** Interiorul de acum, pus în scenă. */
  interior: InteriorEtaj | null = null;

  private corpuri: THREE.Mesh[] = [];
  private facute = new Map<number, Promise<InteriorEtaj>>();
  private lasat = false;

  constructor(private readonly s3: THREE.Object3D, private readonly fatade: readonly Fatada3[],
              private readonly planuri: readonly StoreyPlan[], private readonly opt: OptiuniInterior) {}

  /** Pune planul pe materialele CLĂDIRII (`cladire`: obiectele ei din scenă —
   *  nu terenul, copacii, mașinile) și ține minte corpurile plin, care se
   *  ascund pe etaj. `intuneric`: materialul încăperii din spatele geamului. */
  pregateste(cladire: readonly THREE.Object3D[], intuneric: THREE.Material) {
    const materiale = new Set<THREE.Material>();
    for (const o of cladire) {
      o.traverse((x) => {
        const m = x as THREE.Mesh;
        if (!m.isMesh && !(x as THREE.Line).isLine) return;
        if (m.userData.corpEtaj) this.corpuri.push(m);
        if (m.userData.netaiat) return;
        const mt = m.material;
        for (const y of Array.isArray(mt) ? mt : [mt]) if (y) materiale.add(y);
      });
    }
    for (const mt of materiale) mt.clippingPlanes = [mt === intuneric ? this.jos : this.sus];
  }

  /** Taie la etajul `nivel` (`null` = toată clădirea); întoarce cotele lui. */
  aplica(nivel: number | null) {
    const c = nivel === null ? null : cotaEtajului(this.fatade, nivel);
    this.cote = c && nivel !== null ? { nivel, ...c } : null;
    this.sus.constant = c ? c.sus : FARA_TAIERE;
    this.jos.constant = c ? c.jos + 0.01 : FARA_TAIERE;
    // corpul plin: rămân numai bucățile de sub pardoseala etajului ales
    for (const m of this.corpuri) m.visible = !c || (m.userData.corpEtaj as { sus: number }).sus <= c.jos + 0.01;
    return this.cote;
  }

  /** Interiorul etajului `nivel`, făcut o dată și ținut minte. */
  interiorul(nivel: number): Promise<InteriorEtaj> | null {
    if (this.lasat) return null;
    let p = this.facute.get(nivel);
    if (!p) {
      const c = cotaEtajului(this.fatade, nivel);
      if (!c) return null;
      p = construiesteInteriorul(this.planuri, this.fatade, nivel, c.sus, this.opt);
      this.facute.set(nivel, p);
      this.uita(nivel);
    } else {
      // folosit acum: la coada listei, ultimul de aruncat
      this.facute.delete(nivel);
      this.facute.set(nivel, p);
    }
    return p;
  }

  /** Pune în scenă interiorul etajului tăiat, când e gata, și pregătește
   *  vecinii; întoarce funcția care îl scoate. */
  arata(): () => void {
    const c = this.cote;
    if (!c) return () => {};
    let viu = true;
    const p = this.interiorul(c.nivel);
    p?.then((int) => {
      if (!viu || this.lasat) return;
      this.s3.add(int.grup);
      this.interior = int;
      const liber = (f: () => void) => (window.requestIdleCallback ? window.requestIdleCallback(f) : setTimeout(f, 120));
      liber(() => { if (viu) void this.interiorul(c.nivel + 1); liber(() => { if (viu) void this.interiorul(c.nivel - 1); }); });
    });
    return () => {
      viu = false;
      p?.then((int) => this.s3.remove(int.grup));
      this.interior = null;
    };
  }

  /** Taie și un obiect adăugat după (evidențierea, cotele): fără planul ăsta,
   *  evidențierea unui element de deasupra ar pluti peste etajul tăiat. */
  taie(o: THREE.Object3D) {
    o.traverse((x) => {
      const mt = (x as THREE.Mesh).material;
      for (const y of Array.isArray(mt) ? mt : mt ? [mt] : []) {
        if (!(y.clippingPlanes ?? []).includes(this.sus)) y.clippingPlanes = [...(y.clippingPlanes ?? []), this.sus];
      }
    });
  }

  /** Loviturile unei raze care se văd: nimic din ce e tăiat, iar un perete
   *  interior oprește raza (un clic în cameră nu alege fereastra din spate). */
  vizibile(raza: THREE.Raycaster, plase: readonly THREE.Object3D[]): THREE.Intersection[] {
    const c = this.cote;
    const oc = this.interior?.ocluzie ?? [];
    const toate = raza.intersectObjects(c ? [...plase, ...oc] : (plase as THREE.Object3D[]), false)
      .filter((h) => !c || h.point.z <= c.sus + 0.01);
    if (toate.length && oc.includes(toate[0].object as THREE.Mesh)) return [];
    return toate;
  }

  /** Apartamentul de sub rază, pe pardoseala etajului tăiat. */
  apartamentul(raza: THREE.Raycaster): ApartamentEtaj | null {
    const int = this.interior;
    if (!int || !this.cote) return null;
    const q = new THREE.Vector3();
    if (!raza.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 0, 1), -this.cote.jos), q)) return null;
    return int.apartamente.find((a) => inPoligon(q.x, q.y, a.poly)) ?? null;
  }

  /** Aruncă interioarele vechi, peste `ETAJE_TINUTE` (nu pe cel cerut acum). */
  private uita(pastreaza: number) {
    for (const [n, p] of this.facute) {
      if (this.facute.size <= ETAJE_TINUTE) break;
      if (n === pastreaza || n === this.cote?.nivel) continue;
      this.facute.delete(n);
      p.then((int) => { this.s3.remove(int.grup); int.elibereaza(); });
    }
  }

  /** Scena se închide: tot ce s-a făcut se eliberează. */
  elibereaza() {
    this.lasat = true;
    for (const p of this.facute.values()) p.then((int) => int.elibereaza());
    this.facute.clear();
    this.interior = null;
  }
}
